import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function EyeIcon({ open }) {
  return open ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1 = email, 2 = OTP, 3 = new password
  const [identifier, setIdentifier] = useState('');
  const [resolvedEmail, setResolvedEmail] = useState(''); // email returned by backend
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const inputClass = "w-full px-4 py-3 bg-[#1F2937] border border-[#374151] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500";

  // Step 1 — send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error('Enter your email or phone'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/forgot-password/send-otp`, { identifier: identifier.trim() });
      setResolvedEmail(res.data.email);
      setResendCooldown(60);
      setStep(2);
      toast.success('Verification code sent!');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send code';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — resend OTP
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${API_URL}/auth/forgot-password/send-otp`, { identifier: identifier.trim() });
      toast.success('New code sent!');
      setResendCooldown(60);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to resend';
      toast.error(typeof msg === 'string' ? msg : 'Failed to resend');
    }
  };

  // Step 2 — verify OTP (just proceed to step 3, actual verify happens on submit)
  const handleOtpNext = (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setStep(3);
  };

  // Step 3 — reset password
  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        identifier: identifier.trim(),
        otp,
        new_password: newPassword,
      });
      toast.success('Password updated! Please sign in.');
      navigate('/login');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Reset failed';
      toast.error(typeof msg === 'string' ? msg : 'Reset failed');
      if (err?.response?.status === 400) {
        // OTP invalid/expired — go back to OTP step
        setStep(2);
        setOtp('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-1">
            <Logo size="xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">TradeReflex</h1>
          <p className="text-gray-400">Reset your password</p>
        </div>

        <div className="bg-[#111827] backdrop-blur-xl border border-[#1F2937] rounded-2xl p-8">

          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  step > s ? 'bg-emerald-600 text-white' :
                  step === s ? 'bg-emerald-500 text-white' :
                  'bg-[#1F2937] text-gray-500'
                }`}>{step > s ? '✓' : s}</div>
                {s < 3 && <div className={`flex-1 h-0.5 rounded ${step > s ? 'bg-emerald-600' : 'bg-[#1F2937]'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1 — Email / Phone */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-semibold text-white mb-1">Find your account</h2>
              <p className="text-gray-400 text-sm mb-6">Enter your email or phone and we'll send a reset code.</p>
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email or Phone</label>
                  <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required
                    className={inputClass} placeholder="Email or 10-digit phone" autoFocus />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
            </>
          )}

          {/* Step 2 — OTP */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-semibold text-white mb-1">Enter the code</h2>
              <p className="text-gray-400 text-sm mb-6">
                We sent a 6-digit code to{' '}
                <span className="text-emerald-400 font-medium">{resolvedEmail}</span>
              </p>
              <form onSubmit={handleOtpNext} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Verification code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-2 py-4 bg-[#1F2937] border border-[#374151] rounded-lg text-white text-center text-xl font-bold tracking-[0.3em] placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="------"
                    autoFocus
                  />
                </div>
                <button type="submit" disabled={otp.length !== 6}
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50">
                  Continue
                </button>
              </form>
              <div className="mt-5 text-center">
                <p className="text-gray-500 text-sm mb-2">Didn't receive the code?</p>
                <button onClick={handleResend} disabled={resendCooldown > 0}
                  className="text-emerald-400 hover:text-emerald-300 text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed">
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
              <div className="mt-3 text-center">
                <button onClick={() => { setStep(1); setOtp(''); }} className="text-gray-500 hover:text-gray-300 text-sm">
                  ← Change email / phone
                </button>
              </div>
            </>
          )}

          {/* Step 3 — New Password */}
          {step === 3 && (
            <>
              <h2 className="text-xl font-semibold text-white mb-1">Set new password</h2>
              <p className="text-gray-400 text-sm mb-6">Choose a strong password for your account.</p>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} required
                      className={inputClass} placeholder="Min. 6 characters" />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                  <div className="relative">
                    <input type={showConfirm ? 'text' : 'password'} value={confirm}
                      onChange={(e) => setConfirm(e.target.value)} required
                      className={inputClass} placeholder="Repeat new password" />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50">
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
              <div className="mt-3 text-center">
                <button onClick={() => setStep(2)} className="text-gray-500 hover:text-gray-300 text-sm">
                  ← Back to code entry
                </button>
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-400 hover:text-blue-300 text-sm">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
